import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import type { LocalInterview } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import type InternalInterviewListEntryView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewListEntryView';
import AbandonPreparationAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/AbandonPreparationAction';
import ElementsSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection';
import GatherSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GatherSection';
import GenerateSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection';
import PreparationCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/PreparationCard';
import PreparationHeader from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/PreparationHeader';
import usePreparationLocalEntries from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Hooks/usePreparationLocalEntries';
import AddFromDossierSheet from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Sheets/AddFromDossierSheet';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import { css } from '@emotion/react';
import * as React from 'react';

const screenStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.375rem;
	width: 100%;
	max-width: 48.75rem;
`;

const archivedNoticeStyle = css`
	padding: 0.875rem 1.125rem;
	border-radius: ${theme.radii.large};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.solid};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
`;

const abandonStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	align-items: flex-start;
	padding-top: 0.25rem;
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparation: WorkflowRunView;
	/** Permission to add, remove, abandon and capture, and the dossier is not archived. */
	readonly canWrite: boolean;
	/** Permission to launch the generation and retry a document, and the dossier is not archived. */
	readonly canLaunch: boolean;
	readonly isArchived: boolean;
	readonly entries: readonly InternalInterviewListEntryView[];
	readonly localInterviews: readonly LocalInterview[];
	/** Refetches the preparation and the interviews. */
	readonly onRefresh: () => void;
};

/** The nominal layout of the preparation: header, « Rassembler » with its elements, « Générer », the « Abandonner » link and the sheet. */
const LoadedPreparation: React.FC<Props> = ({
	subject,
	preparation,
	canWrite,
	canLaunch,
	isArchived,
	entries,
	localInterviews,
	onRefresh,
}) => {
	const { drafts, entryByInterviewId } = usePreparationLocalEntries(preparation, entries, localInterviews);

	// A recording leaving the drafts has been sent: the server holds its element.
	const draftCountRef = React.useRef(drafts.length);
	React.useEffect(() => {
		if (drafts.length < draftCountRef.current) {
			onRefresh();
		}

		draftCountRef.current = drafts.length;
	}, [drafts.length, onRefresh]);

	return (
		<div css={screenStyle}>
			<PreparationHeader subject={subject} preparation={preparation} />
			<ConditionalLoader data={isArchived}>
				<div css={archivedNoticeStyle} role="note">
					Ce dossier est archivé : la préparation est en lecture seule.
				</div>
			</ConditionalLoader>
			<PreparationCard
				number={1}
				title="Rassembler"
				hint="Les éléments s’accumulent visite après visite. Ajouter un élément ne génère rien."
			>
				<ConditionalLoader data={preparation.isOpen && canWrite}>
					<GatherSection subject={subject} preparation={preparation} />
				</ConditionalLoader>
				<ElementsSection
					subject={subject}
					preparation={preparation}
					drafts={drafts}
					entryByInterviewId={entryByInterviewId}
					canWrite={canWrite}
				/>
			</PreparationCard>
			<PreparationCard
				number={2}
				title="Générer"
				hint="Un seul clic génère tous les documents : les étapes s’enchaînent d’elles-mêmes. Vous décidez quand vous avez assez d’éléments."
			>
				<GenerateSection subject={subject} preparation={preparation} canLaunch={canLaunch} />
			</PreparationCard>
			<ConditionalLoader data={preparation.canAbandon && canWrite}>
				<div css={abandonStyle}>
					<AbandonPreparationAction subject={subject} preparationId={preparation.id} />
				</div>
			</ConditionalLoader>
			<ConditionalLoader data={canWrite}>
				<AddFromDossierSheet subject={subject} preparation={preparation} />
			</ConditionalLoader>
		</div>
	);
};

export default LoadedPreparation;
